/**
 * Input component for session code with auto-formatting.
 */

import { ChangeEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface SessionCodeInputProps {
  /** Current value (raw, without dashes) */
  value: string
  /** Called with normalized value (no dashes, uppercase) */
  onChange: (value: string) => void
  /** Disable input (e.g., when pre-filled from URL) */
  disabled?: boolean
  /** Error message to display */
  error?: string
  /** Additional CSS classes for input */
  className?: string
  /** @deprecated dark prop removed — new palette is always dark */
  dark?: boolean
}

// Valid characters for session code (no confusing 0/O/1/I/L)
// const VALID_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'

/**
 * Format raw input as ABC-123-XYZ for display.
 */
function formatForDisplay(raw: string): string {
  const clean = raw.replace(/[^A-Z0-9]/gi, '').toUpperCase()
  const parts: string[] = []

  for (let i = 0; i < clean.length && i < 9; i += 3) {
    parts.push(clean.slice(i, i + 3))
  }

  return parts.join('-')
}

/**
 * Normalize input to raw format (no dashes, uppercase).
 */
function normalize(input: string): string {
  return input.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 9)
}

export function SessionCodeInput({
  value,
  onChange,
  disabled = false,
  error,
  className = '',
}: SessionCodeInputProps) {
  const { t } = useTranslation()

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const normalized = normalize(e.target.value)
    onChange(normalized)
  }, [onChange])

  const displayValue = formatForDisplay(value)

  return (
    <div className="w-full">
      <Input
        type="text"
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={t('join.sessionCodeFormatPlaceholder')}
        maxLength={11}
        autoCapitalize="characters"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        inputMode="text"
        className={cn(
          // Same face as SessionCode: the player reads the code off the TV and
          // retypes it here, so both must render the glyphs identically.
          "text-3xl md:text-2xl text-center tracking-widest font-mono uppercase h-14",
          "bg-input border-border text-foreground focus-visible:ring-ring",
          error && "border-destructive",
          className
        )}
        aria-label={t("join.sessionCodeAriaLabel")}
        aria-invalid={!!error}
        aria-describedby={error ? 'session-code-error' : undefined}
      />
      {error && (
        <p id="session-code-error" className="mt-1 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  )
}
